import { z } from 'zod';
import {
  CONNECTOR_CHANNEL_PREFIX,
  connectorConfigInputSchema,
  createConnectorSchema,
  hasPermission,
  testConnectorSchema,
  updateConnectorSchema,
  type AdminAction,
  type ConnectorConfig,
  type ConnectorConfigDto,
  type ConnectorDto,
  type ConnectorStatusDto,
  type ConnectorTestResult,
  type IndicatorDto,
  type SecretHint,
  type SourceSensorDto,
} from '@atnt/shared';
import { api, ApiError } from '@/shared/api/client';
import { devicesQuery, deviceKeys } from '@/features/devices/api';
import type { ConnectorsBackend, ConnectorsContext } from '../backend';
import { connectorKeys, connectorStatusQuery, tenantBase, tenantDetailsQuery } from '../keys';
import { webhookUrl } from '../util';
import { buildSensors, seedLog, seedOrg, seedTenant, type IndicatorLite } from './seed';
import { loadOrg, loadTenant, logEntry, newId, saveOrg, saveTenant, type ConnectorRecord, type SensorRecord, type TenantStore } from './store';

/**
 * In-browser connectors backend (UI-first; the API will implement the same contract later).
 *
 * Real parts, read through the query client:
 *  - status / last sync / last error of the `atnt_unified` connector ← `GET /tenants/:t/connector-status`
 *  - its site code ← the tenant's `externalSiteId` (`GET /tenants/:t`)
 *  - its `hydro:*` source sensors are bound through the real indicator channel (`externalChannelId`, `PATCH …/indicators/:id`)
 *  - bindings show the tenant's real physical indicators (`GET /tenants/:t/devices`)
 * Everything else (other connectors, raw source sensors, bindings of raw columns, log, tests) lives in localStorage.
 *
 * Mock test rules: a URL / host containing "example", "fail" or "invalid" fails (REST → HTTP 401, MQTT → refused,
 * OPC UA → BadSecurityChecksFailed, Modbus → timeout); everything else connects.
 */

const delay = (min = 300, max = 800) => new Promise((r) => setTimeout(r, min + Math.random() * (max - min)));

const fail = (status: number, code: string, detail: string, errors?: Array<{ path: string; message: string }>) =>
  new ApiError({ type: 'about:blank', title: detail, status, detail, code, errors });

function parse<T extends z.ZodType>(schema: T, input: unknown): z.output<T> {
  const r = schema.safeParse(input);
  if (r.success) return r.data;
  throw fail(400, 'validation', 'Validation failed', r.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })));
}

/* ───────────── context: stores, rights, real data ───────────── */

const isOrgAdmin = ({ me, tenant }: ConnectorsContext) => me.isPlatformAdmin || me.organizations.some((o) => o.id === tenant.organizationId && o.isAdmin);
const can = (ctx: ConnectorsContext, action: AdminAction) => hasPermission(ctx.tenant.permissions, { panel: 'admin', module: 'connectors', action });
function requireRight(ctx: ConnectorsContext, action: AdminAction, record?: ConnectorRecord) {
  if (!can(ctx, action)) throw fail(403, 'forbidden', `connectors.${action}`);
  if (record?.scope === 'organization' && !isOrgAdmin(ctx)) throw fail(403, 'org_admin_only', 'Organization connectors are managed by organization admins');
}

const creator = ({ me }: ConnectorsContext) => ({ id: me.user.id, fullName: me.user.fullName });

function stores(ctx: ConnectorsContext) {
  const org = loadOrg(ctx.tenant.organizationId, () => seedOrg(ctx.tenant.organizationId, null));
  const tenant = loadTenant(ctx.tenant.id, () => seedTenant(ctx.tenant.id, ctx.tenant.organizationId, null));
  return { org, tenant };
}

interface RealData {
  indicators: IndicatorLite[];
  status: ConnectorStatusDto | null;
  siteCode: string | null;
}

async function realData(ctx: ConnectorsContext, fresh = false): Promise<RealData> {
  const opts = fresh ? { staleTime: 0 } : {};
  const [devices, status, details] = await Promise.allSettled([
    ctx.qc.fetchQuery({ ...devicesQuery(ctx.tenant.id), ...opts }),
    ctx.qc.fetchQuery({ ...connectorStatusQuery(ctx.tenant.id), staleTime: fresh ? 0 : 10_000 }),
    ctx.qc.fetchQuery({ ...tenantDetailsQuery(ctx.tenant.id), staleTime: 30_000 }),
  ]);
  const indicators: IndicatorLite[] =
    devices.status === 'fulfilled'
      ? devices.value.flatMap((d) =>
          d.indicators
            .filter((i: IndicatorDto) => i.kind === 'physical')
            .map((i) => ({ id: i.id, deviceId: d.id, deviceName: d.name, name: i.name, unit: i.unit, externalChannelId: i.externalChannelId })),
        )
      : [];
  return {
    indicators,
    status: status.status === 'fulfilled' ? status.value : null,
    siteCode: details.status === 'fulfilled' ? details.value.externalSiteId : status.status === 'fulfilled' ? status.value.externalSiteId : null,
  };
}

const siteOf = (ctx: ConnectorsContext, record: ConnectorRecord, real: RealData) =>
  record.config.type === 'atnt_unified' ? (real.siteCode ?? ctx.tenant.slug) : null;
const sensorsKey = (ctx: ConnectorsContext, record: ConnectorRecord, real: RealData) => {
  const site = siteOf(ctx, record, real);
  return site ? `${record.id}@${site}` : record.id;
};
const isHydro = (record: ConnectorRecord, s: Pick<SensorRecord, 'channel'>) =>
  record.type === 'atnt_unified' && Boolean(s.channel?.toLowerCase().startsWith(CONNECTOR_CHANNEL_PREFIX));

/** Sensors of a connector for this tenant (seeded on first use), with the real hydro bindings applied. */
function sensorsOf(ctx: ConnectorsContext, tenant: TenantStore, record: ConnectorRecord, real: RealData): SensorRecord[] {
  const key = sensorsKey(ctx, record, real);
  let list = tenant.sensors[key];
  if (!list) {
    list = buildSensors(record, siteOf(ctx, record, real), real.indicators);
    tenant.sensors[key] = list;
    tenant.logs[record.id] ??= seedLog(record, list.length);
    saveTenant(ctx.tenant.id, tenant);
  }
  return list.map((s) => {
    if (!isHydro(record, s)) {
      // a binding to an indicator that no longer exists is dropped on read
      return s.binding && real.indicators.length && !real.indicators.some((i) => i.id === s.binding!.indicatorId) ? { ...s, binding: null } : s;
    }
    const bound = real.indicators.find((i) => i.externalChannelId?.trim().toLowerCase() === s.channel!.toLowerCase());
    return { ...s, binding: bound ? { indicatorId: bound.id, deviceId: bound.deviceId } : null };
  });
}

/* ───────────── DTO ───────────── */

function hint(value: string | undefined | null): SecretHint {
  if (!value) return { set: false, hint: null };
  const parts = value.split('.');
  if (parts[0] === 'atnt' && parts.length >= 3) return { set: true, hint: `atnt.${parts[1]}.••••` };
  return { set: true, hint: `••••${value.slice(-4)}` };
}

function maskConfig(record: ConnectorRecord, siteCode: string | null): ConnectorConfigDto {
  const c = record.config;
  switch (c.type) {
    case 'atnt_unified':
      return { ...c, apiKey: hint(c.apiKey), siteCode };
    case 'rest':
      return {
        ...c,
        auth:
          c.auth.kind === 'none'
            ? { kind: 'none' }
            : c.auth.kind === 'header'
              ? { kind: 'header', headerName: c.auth.headerName, value: hint(c.auth.value) }
              : { kind: 'bearer', token: hint(c.auth.token) },
      };
    case 'mqtt':
    case 'opcua':
      return { ...c, username: c.username ?? null, password: hint(c.password) };
    case 'webhook':
      return { type: 'webhook', payloadFormat: c.payloadFormat, endpointUrl: webhookUrl(record.id), secret: hint(c.secret) };
    default:
      return c;
  }
}

function liveStatus(record: ConnectorRecord, real: RealData): Pick<ConnectorRecord, 'status' | 'lastSyncAt' | 'lastError'> {
  const s = real.status;
  if (record.type !== 'atnt_unified' || !record.enabled || !s || record.status === 'checking') return record;
  if (!s.source) return { status: 'error', lastSyncAt: record.lastSyncAt, lastError: s.health?.lastError ?? 'Live source is not running (LIVE_SOURCE / UNIFIED_BASE_URL / UNIFIED_API_KEY)' };
  return s.health?.ok
    ? { status: 'connected', lastSyncAt: s.health.lastSuccessAt ?? record.lastSyncAt, lastError: null }
    : { status: 'error', lastSyncAt: s.health?.lastSuccessAt ?? record.lastSyncAt, lastError: s.health?.lastError ?? null };
}

function toDto(ctx: ConnectorsContext, tenant: TenantStore, record: ConnectorRecord, real: RealData): ConnectorDto {
  const sensors = sensorsOf(ctx, tenant, record, real);
  const { config: _secrets, ...rest } = record;
  return {
    ...rest,
    ...liveStatus(record, real),
    sensorsTotal: sensors.length,
    sensorsBound: sensors.filter((s) => s.binding).length,
    config: maskConfig(record, siteOf(ctx, record, real)),
  };
}

const sensorDto = (s: SensorRecord): SourceSensorDto => ({ ...s });

/* ───────────── records ───────────── */

function find(ctx: ConnectorsContext, id: string) {
  const { org, tenant } = stores(ctx);
  const inOrg = org.connectors.find((c) => c.id === id);
  const record = inOrg ?? tenant.connectors.find((c) => c.id === id);
  if (!record) throw fail(404, 'not_found', 'Connector not found');
  const save = () => (inOrg ? saveOrg(ctx.tenant.organizationId, org) : saveTenant(ctx.tenant.id, tenant));
  return { record, org, tenant, save };
}

function pushLog(ctx: ConnectorsContext, tenant: TenantStore, id: string, entry: ReturnType<typeof logEntry>) {
  tenant.logs[id] = [entry, ...(tenant.logs[id] ?? [])].slice(0, 300);
  saveTenant(ctx.tenant.id, tenant);
}

/** Keep stored secrets the input omitted (write-only fields). */
function mergeSecrets(prev: ConnectorConfig | undefined, next: ConnectorConfig): ConnectorConfig {
  if (!prev || prev.type !== next.type) return next;
  switch (next.type) {
    case 'atnt_unified':
      return { ...next, apiKey: next.apiKey ?? (prev as typeof next).apiKey };
    case 'mqtt':
    case 'opcua':
      return { ...next, password: next.password ?? (prev as typeof next).password };
    case 'webhook':
      return { ...next, secret: next.secret ?? (prev as typeof next).secret };
    case 'rest': {
      const p = (prev as typeof next).auth;
      if (next.auth.kind === 'header' && p.kind === 'header') return { ...next, auth: { ...next.auth, value: next.auth.value ?? p.value } };
      if (next.auth.kind === 'bearer' && p.kind === 'bearer') return { ...next, auth: { ...next.auth, token: next.auth.token ?? p.token } };
      return next;
    }
    default:
      return next;
  }
}

/* ───────────── test ───────────── */

function endpointOf(c: ConnectorConfig) {
  switch (c.type) {
    case 'atnt_unified':
    case 'rest':
      return c.baseUrl;
    case 'mqtt':
      return c.brokerUrl;
    case 'opcua':
      return c.endpointUrl;
    case 'modbus_tcp':
      return c.host;
    default:
      return '';
  }
}

const FAILURES: Record<string, string> = {
  rest: 'HTTP 401 Unauthorized: token rad etildi',
  atnt_unified: 'HTTP 401 Unauthorized: X-API-Key rad etildi',
  mqtt: 'Connection refused: not authorized (rc=5)',
  opcua: 'BadSecurityChecksFailed (0x80130000)',
  modbus_tcp: 'connect ETIMEDOUT 502',
};

const checkedAt = () => new Date().toISOString();

async function probe(ctx: ConnectorsContext, config: ConnectorConfig, sensorsFound: () => number): Promise<ConnectorTestResult> {
  if (config.type === 'atnt_unified') {
    const started = performance.now();
    try {
      const s = await ctx.qc.fetchQuery({ ...connectorStatusQuery(ctx.tenant.id), staleTime: 0 });
      const latencyMs = Math.round(performance.now() - started);
      await delay(200, 400);
      if (!s.source) return { ok: false, latencyMs, sensorsFound: null, message: s.health?.lastError ?? 'Live source is not running', checkedAt: checkedAt() };
      return s.health?.ok
        ? { ok: true, latencyMs, sensorsFound: sensorsFound(), message: s.source === 'mock' ? 'LIVE_SOURCE=mock' : null, checkedAt: checkedAt() }
        : { ok: false, latencyMs, sensorsFound: null, message: s.health?.lastError ?? null, checkedAt: checkedAt() };
    } catch (err) {
      return { ok: false, latencyMs: null, sensorsFound: null, message: err instanceof Error ? err.message : String(err), checkedAt: checkedAt() };
    }
  }
  const latencyMs = Math.round(120 + Math.random() * 700);
  await delay(latencyMs, latencyMs + 200);
  if (config.type === 'webhook') return { ok: true, latencyMs: null, sensorsFound: 0, message: null, checkedAt: checkedAt() };
  if (config.type === 'file_import') return { ok: true, latencyMs: null, sensorsFound: config.columns.length, message: null, checkedAt: checkedAt() };
  if (/example|fail|invalid/i.test(endpointOf(config))) return { ok: false, latencyMs, sensorsFound: null, message: FAILURES[config.type] ?? 'Error', checkedAt: checkedAt() };
  return { ok: true, latencyMs, sensorsFound: sensorsFound(), message: null, checkedAt: checkedAt() };
}

function applyTest(record: ConnectorRecord, result: ConnectorTestResult) {
  if (!record.enabled) return;
  record.status = result.ok ? 'connected' : 'error';
  record.lastError = result.ok ? null : result.message;
  if (result.ok) record.lastSyncAt = result.checkedAt;
}

const testLog = (r: ConnectorTestResult) =>
  r.ok
    ? logEntry('info', 'test.ok', 'Connection test passed', { ms: r.latencyMs ?? 0, sensors: r.sensorsFound ?? 0 })
    : logEntry('error', 'test.failed', r.message ?? 'Connection test failed', { error: r.message ?? '' });

/* ───────────── backend ───────────── */

export const mockBackend: ConnectorsBackend = {
  async list(ctx) {
    requireRight(ctx, 'read');
    const [real] = await Promise.all([realData(ctx), delay()]);
    const { org, tenant } = stores(ctx);
    return [...org.connectors, ...tenant.connectors].map((r) => toDto(ctx, tenant, r, real));
  },

  async get(ctx, id) {
    requireRight(ctx, 'read');
    const [real] = await Promise.all([realData(ctx), delay(150, 400)]);
    const { record, tenant } = find(ctx, id);
    return toDto(ctx, tenant, record, real);
  },

  async create(ctx, input) {
    const data = parse(createConnectorSchema, input);
    requireRight(ctx, 'create');
    if (data.scope === 'organization' && !isOrgAdmin(ctx)) throw fail(403, 'org_admin_only', 'Organization connectors are managed by organization admins');
    const { org, tenant } = stores(ctx);
    if (data.config.type === 'atnt_unified' && [...org.connectors, ...tenant.connectors].some((c) => c.type === 'atnt_unified')) {
      throw fail(409, 'unified_exists', 'This reservoir is already connected to the unified server');
    }
    const id = data.id ?? newId();
    if ([...org.connectors, ...tenant.connectors].some((c) => c.id === id)) throw fail(409, 'id_taken', 'Connector id already exists');
    const now = new Date().toISOString();
    const record: ConnectorRecord = {
      id,
      name: data.name,
      description: data.description ?? null,
      type: data.config.type,
      scope: data.scope,
      organizationId: ctx.tenant.organizationId,
      tenantId: data.scope === 'tenant' ? ctx.tenant.id : null,
      enabled: data.enabled,
      status: data.enabled ? 'checking' : 'disabled',
      lastSyncAt: null,
      lastError: null,
      config: data.config,
      createdBy: creator(ctx),
      createdAt: now,
      updatedAt: now,
    };
    const real = await realData(ctx);
    const sensors = buildSensors(record, siteOf(ctx, record, real), [], []);
    tenant.sensors[sensorsKey(ctx, record, real)] = sensors;
    tenant.logs[id] = [logEntry('info', 'connector.created', 'Connector created')];
    if (data.enabled) {
      const result = await probe(ctx, record.config, () => sensors.length);
      applyTest(record, result);
      tenant.logs[id] = [testLog(result), ...tenant.logs[id]!];
    }
    if (record.scope === 'organization') {
      org.connectors.push(record);
      saveOrg(ctx.tenant.organizationId, org);
    } else tenant.connectors.push(record);
    saveTenant(ctx.tenant.id, tenant);
    return toDto(ctx, tenant, record, real);
  },

  async update(ctx, id, input) {
    const data = parse(updateConnectorSchema, input);
    const { record, tenant, save } = find(ctx, id);
    requireRight(ctx, 'update', record);
    if (data.config && data.config.type !== record.type) throw fail(400, 'type_fixed', 'Connector type cannot be changed');
    const real = await realData(ctx);
    const prevKey = sensorsKey(ctx, record, real);
    const wasEnabled = record.enabled;
    if (data.name !== undefined) record.name = data.name;
    if (data.description !== undefined) record.description = data.description ?? null;
    if (data.config) record.config = mergeSecrets(record.config, data.config);
    if (data.enabled !== undefined) record.enabled = data.enabled;
    record.updatedAt = new Date().toISOString();
    if (data.config && record.type !== 'atnt_unified') {
      tenant.sensors[prevKey] = buildSensors(record, null, real.indicators, tenant.sensors[prevKey] ?? []);
    }
    const entries = [];
    if (data.name !== undefined || data.description !== undefined || data.config) entries.push(logEntry('info', 'connector.updated', 'Settings updated'));
    if (wasEnabled !== record.enabled) entries.push(logEntry('info', record.enabled ? 'connector.enabled' : 'connector.disabled', record.enabled ? 'Connector enabled' : 'Connector disabled'));
    if (!record.enabled) record.status = 'disabled';
    else if (!wasEnabled || data.config) {
      const count = tenant.sensors[prevKey]?.length ?? 0;
      const result = await probe(ctx, record.config, () => count);
      applyTest(record, result);
      entries.unshift(testLog(result));
    }
    save();
    for (const e of entries.toReversed()) pushLog(ctx, tenant, id, e);
    return toDto(ctx, tenant, record, real);
  },

  async remove(ctx, id) {
    await delay();
    const { record, org, tenant } = find(ctx, id);
    requireRight(ctx, 'delete', record);
    if (record.scope === 'organization') {
      org.connectors = org.connectors.filter((c) => c.id !== id);
      saveOrg(ctx.tenant.organizationId, org);
    } else tenant.connectors = tenant.connectors.filter((c) => c.id !== id);
    for (const key of Object.keys(tenant.sensors)) if (key === id || key.startsWith(`${id}@`)) delete tenant.sensors[key];
    delete tenant.logs[id];
    saveTenant(ctx.tenant.id, tenant);
  },

  async test(ctx, id, input) {
    const data = parse(testConnectorSchema, input);
    const found = id ? find(ctx, id) : null;
    if (found) requireRight(ctx, 'update');
    else requireRight(ctx, 'create');
    const config = data.config ? mergeSecrets(found?.record.config, parse(connectorConfigInputSchema, data.config)) : found?.record.config;
    if (!config) throw fail(400, 'validation', 'config is required');
    const real = await realData(ctx, true);
    const count = () =>
      found ? sensorsOf(ctx, found.tenant, found.record, real).length : buildSensors({ id: 'draft', config }, real.siteCode ?? ctx.tenant.slug, []).length;
    const result = await probe(ctx, config, count);
    if (found && !data.config) {
      applyTest(found.record, result);
      found.save();
      pushLog(ctx, found.tenant, found.record.id, testLog(result));
    }
    return result;
  },

  async sensors(ctx, id) {
    requireRight(ctx, 'read');
    const [real] = await Promise.all([realData(ctx), delay(250, 600)]);
    const { record, tenant } = find(ctx, id);
    return sensorsOf(ctx, tenant, record, real).map(sensorDto);
  },

  async bind(ctx, id, sensorId, indicatorId) {
    requireRight(ctx, 'update');
    const { record, tenant } = find(ctx, id);
    const real = await realData(ctx, true);
    const key = sensorsKey(ctx, record, real);
    const list = sensorsOf(ctx, tenant, record, real);
    const sensor = list.find((s) => s.id === sensorId);
    if (!sensor) throw fail(404, 'not_found', 'Sensor not found');
    const target = indicatorId ? real.indicators.find((i) => i.id === indicatorId) : null;
    if (indicatorId && !target) throw fail(400, 'indicator_not_found', 'Physical indicator not found');

    if (isHydro(record, sensor)) {
      // real: the unified-server channel lives on the indicator (`externalChannelId = hydro:<KEY>`)
      const channel = sensor.channel!;
      const patch = (i: IndicatorLite, value: string | null) =>
        api.patch(`${tenantBase(ctx.tenant.id)}/devices/${encodeURIComponent(i.deviceId)}/indicators/${encodeURIComponent(i.id)}`, { externalChannelId: value });
      const current = real.indicators.filter((i) => i.externalChannelId?.trim().toLowerCase() === channel.toLowerCase() && i.id !== indicatorId);
      for (const i of current) await patch(i, null);
      if (target) await patch(target, channel);
      await Promise.all([
        ctx.qc.invalidateQueries({ queryKey: deviceKeys.devices(ctx.tenant.id) }),
        ctx.qc.invalidateQueries({ queryKey: connectorKeys.status(ctx.tenant.id) }),
      ]);
    } else {
      await delay(200, 450);
      const stored = tenant.sensors[key] ?? [];
      tenant.sensors[key] = stored.map((s) => {
        if (s.id === sensorId) return { ...s, binding: target ? { indicatorId: target.id, deviceId: target.deviceId } : null };
        // one source per indicator: a re-bind moves it
        if (target && s.binding?.indicatorId === target.id) return { ...s, binding: null };
        return s;
      });
      saveTenant(ctx.tenant.id, tenant);
    }
    pushLog(
      ctx,
      tenant,
      id,
      target
        ? logEntry('info', 'sensor.bound', 'Sensor bound', { column: sensor.columnName, indicator: `${target.deviceName} · ${target.name}` })
        : logEntry('info', 'sensor.unbound', 'Sensor unbound', { column: sensor.columnName }),
    );
    const binding = target ? { indicatorId: target.id, deviceId: target.deviceId } : null;
    return sensorDto({ ...sensor, binding });
  },

  async importSensors(ctx, id, sensorIds) {
    requireRight(ctx, 'update');
    const [real] = await Promise.all([realData(ctx), delay(600, 1200)]);
    const { record, tenant } = find(ctx, id);
    const picked = sensorsOf(ctx, tenant, record, real).filter((s) => sensorIds.includes(s.id) && !s.binding);
    const result = { devicesCreated: new Set(picked.map((s) => s.node)).size, indicatorsCreated: picked.length, sensorsBound: 0 };
    pushLog(ctx, tenant, id, logEntry('info', 'sensors.imported', 'Import requested (mock)', { devices: result.devicesCreated, sensors: result.indicatorsCreated }));
    return result;
  },

  async log(ctx, id) {
    requireRight(ctx, 'read');
    const [real] = await Promise.all([realData(ctx), delay(200, 500)]);
    const { record, tenant } = find(ctx, id);
    sensorsOf(ctx, tenant, record, real); // seeds the log on first use
    return (tenant.logs[id] ?? []).toSorted((a, b) => b.at.localeCompare(a.at));
  },
};
